import { useState } from "react";
import { Search, Loader2, CheckCircle2, Clock, AlertCircle, XCircle, Archive as ArchiveIcon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const STATUS_META = {
  "Dépôt reçu": { color: "bg-slate-100 text-slate-700 border-slate-200", Icon: CheckCircle2 },
  "Contrôle administratif": { color: "bg-blue-50 text-blue-700 border-blue-100", Icon: Clock },
  "Test anti-plagiat": { color: "bg-amber-50 text-amber-800 border-amber-100", Icon: Clock },
  "En cours d'analyse": { color: "bg-blue-50 text-blue-700 border-blue-100", Icon: Clock },
  "Validé": { color: "bg-green-50 text-green-700 border-green-100", Icon: CheckCircle2 },
  "Validé sous réserve": { color: "bg-emerald-50 text-emerald-700 border-emerald-100", Icon: CheckCircle2 },
  "À corriger": { color: "bg-orange-50 text-orange-700 border-orange-100", Icon: AlertCircle },
  "Refusé": { color: "bg-red-50 text-red-700 border-red-100", Icon: XCircle },
  "Archivé": { color: "bg-slate-100 text-slate-700 border-slate-200", Icon: ArchiveIcon },
};

export function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META["Dépôt reçu"];
  const Icon = m.Icon;
  return (
    <span className={`status-pill ${m.color}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
}

export default function SuiviDepot() {
  const [tracking, setTracking] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setData(null);
    setLoading(true);
    try {
      const { data } = await api.get(`/deposits/track/${encodeURIComponent(tracking.trim())}`);
      setData(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16" data-testid="suivi-page">
      <div className="eyebrow eyebrow-dark">Suivi de dépôt</div>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-unisic-blue">Suivre mon dossier</h1>
      <span className="divider-gold mt-3" />
      <p className="mt-4 text-slate-600">
        Entrez le numéro de suivi reçu lors de votre dépôt pour consulter l'état d'avancement.
      </p>

      <form onSubmit={submit} className="mt-8 flex gap-3 flex-col sm:flex-row" data-testid="form-suivi">
        <input
          value={tracking}
          onChange={(e) => setTracking(e.target.value)}
          placeholder="UNISIC-MEM-2025-2026-000001"
          className="flex-1 border border-slate-300 px-4 py-3 rounded-sm text-sm font-mono focus:outline-none focus:ring-2 focus:ring-unisic-gold"
          data-testid="input-tracking"
        />
        <button type="submit" disabled={!tracking || loading} className="btn-primary" data-testid="btn-track">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Rechercher
        </button>
      </form>

      {error && (
        <div className="mt-6 text-sm text-unisic-red bg-red-50 border border-red-100 px-4 py-3 rounded-sm" data-testid="suivi-error">
          {error}
        </div>
      )}

      {data && (
        <div className="mt-10 paper-card p-8" data-testid="suivi-result">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="eyebrow eyebrow-dark">Numéro</div>
              <div className="mt-1 font-mono text-lg text-unisic-blue">{data.tracking_number}</div>
              <div className="mt-2 text-sm text-slate-600">
                <span className="font-semibold">{data.kind === "memoire" ? "Mémoire" : "Projet tutoré"}</span>
                {data.subject && <> · {data.subject}</>}
              </div>
              <div className="mt-1 text-xs text-slate-500">Année académique : {data.academic_year || "—"}</div>
            </div>
            <StatusBadge status={data.status} />
          </div>

          {data.plagiarism && (
            <div className="mt-6 paper-card p-5 bg-amber-50/50 border-l-4 border-l-unisic-gold">
              <div className="eyebrow eyebrow-dark">Résultat anti-plagiat</div>
              <div className="mt-2 text-sm text-slate-700">
                Similarité :{" "}
                <span className="font-semibold text-unisic-blue">{data.plagiarism.similarity}%</span> ·
                Décision :{" "}
                <span className="font-semibold text-unisic-blue">{data.plagiarism.decision}</span>
              </div>
            </div>
          )}

          <div className="mt-8">
            <div className="eyebrow eyebrow-dark">Historique du dossier</div>
            <ol className="mt-4 relative border-l-2 border-slate-200 pl-6 space-y-6">
              {(data.history || []).map((h, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[31px] top-1 w-3 h-3 rounded-full bg-unisic-gold" />
                  <div className="flex items-center gap-3 flex-wrap">
                    <StatusBadge status={h.status} />
                    <span className="text-xs text-slate-500">{new Date(h.at).toLocaleString("fr-FR")}</span>
                  </div>
                  {h.comment && <p className="mt-2 text-sm text-slate-600">{h.comment}</p>}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
